/**
 * useShellRail - the order rail's rows and the gestures around them.
 *
 * - THE ROWS (v8.317.0): the tracker's order first, then the rest of the
 *   roster, each in its team's livery. A projection.
 * - WHO IS UNDER THE POINTER (v8.319.0): the row lights when a token is
 *   hovered and the token when a row is. Local, never synced.
 * - TRACK EVERYONE (v8.332.0): the roster members NOT IN THE ORDER, admitted
 *   in one press through the batch the deploy path uses.
 * - TAB WALKS THE RAIL (v8.320.0): the next target is the next row of the
 *   order, so the key and the column agree.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useState, useMemo, useCallback } from 'react';
import * as settings from '../engine/settings/resolve';
import { liveryByCombatant } from '../engine/roster/team';
import { sittingOut } from '../engine/turnTracker';
import { useOrderRail } from './useOrderRail';
import { isObject } from '../engine/bodyShape';

/** The rows a Track Everyone press would admit: in the roster, not in the order, not custom. */
export function untrackedOf(railRows, trackerActive) {
  // Never a hidden figure: it has no seat until it is revealed (ADR 2026-10-07).
  // Nor an Object (2026-10-26): it takes no turn, so the door would refuse it anyway - and the count would lie.
  const seatable = (r) => !r.inOrder && r.combatantId && !r.isCustom && !r.hidden && !isObject(r.target);
  return trackerActive ? railRows.filter(seatable) : [];
}

/** The target `dir` rows along the rail from the current one, wrapping; null with no rows. */
export function railStep(railRows, activeTab, dir) {
  const ids = railRows.filter((r) => r.combatantId).map((r) => r.combatantId);
  if (!ids.length) return null;
  return ids[(ids.indexOf(activeTab) + dir + ids.length) % ids.length];
}

/** Admit every untracked row, one undo step, initiative from their sheets. */
function useTrackEveryone(untrackedRows, { isLocked, turnTracker, sheetForCombatant }) {
  return useCallback(() => {
    if (isLocked('turnTracker') || untrackedRows.length === 0) return;
    turnTracker.addTabParticipantBatch(
      untrackedRows.map((r) => ({ combatantId: r.combatantId, name: r.name, sheet: sheetForCombatant(r.combatantId) })),
    );
  }, [isLocked, untrackedRows, turnTracker, sheetForCombatant]);
}

/**
 * @param {object} args - tabs, roster, actorId, activeTab, setActiveTab,
 *   turnTracker, permissions, isLocked, sheetForCombatant.
 */
export default function useShellRail(args) {
  const { tabs, roster, actorId, activeTab, setActiveTab, turnTracker } = args;
  const [hoveredId, setHoveredId] = useState(null);
  const railLiveries = useMemo(() => liveryByCombatant(roster), [roster]);
  const { participants, active } = turnTracker.trackerState;
  // Who is surprised this round - derived from the tracker, like the downed, never stored on a row.
  const { round, surprise } = turnTracker.trackerState;
  const out = useMemo(() => sittingOut({ round, surprise }), [round, surprise]);
  const railRows = useOrderRail({
    tabs,
    participants,
    trackerActive: active,
    sittingOut: out,
    hiddenIds: args.hidden?.hiddenIds,
    actorId,
    targetId: activeTab,
    liveries: railLiveries,
    skipDownedTurns: settings.resolveSkipDownedTurns(args.permissions),
  });
  const untrackedRows = useMemo(() => untrackedOf(railRows, active), [railRows, active]);
  const handleTrackEveryone = useTrackEveryone(untrackedRows, args);
  const cycleRail = useCallback(
    (dir) => {
      const next = railStep(railRows, activeTab, dir);
      if (next !== null) setActiveTab(next);
    },
    [railRows, activeTab, setActiveTab],
  );
  return { hoveredId, setHoveredId, railRows, untrackedRows, handleTrackEveryone, cycleRail };
}
